import { useEffect, useRef, useState } from 'react'
import { FetchBannerApiData } from '@ClientBase/Generic/FetchBannerPoolApiData'
import { Props_SnippetBrands } from './Brands.props'
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules'

interface Banner {
    banners: Brand[]
    cssClass: string
    id: string
    height: number
    width: number
    isExactHeight: boolean
    isExactWidth: boolean
}

interface Brand {
    altTagText: string
    cssClass: string
    customHtml: string
    id: string
    imageId: string
    sortOrder: number
    targetUrl: string
    title: string
}

const fetchBrands = async (snippet: React.RefObject<HTMLElement | null>, setBrands: (brands: Brand[]) => void) => {
    try {
        const result: Banner = await FetchBannerApiData({
            displayCount: 99,
            dataIdentifier: 'DI-Brands',
            includeCustomHTML: false,
        })
        const sortedBrands = result.banners.slice().sort((a, b) => a.sortOrder - b.sortOrder)
        setBrands(sortedBrands)
    } catch (error) {
        console.error('Error fetching brand banner data:', error)
    } finally {
        snippet.current?.classList.add('snippet-brands--loaded')
    }
}

const SnippetBrands = (props: Props_SnippetBrands) => {
    const { heading } = props

    const refSnippet = useRef<HTMLElement | null>(null)
    const [brands, setBrands] = useState<Brand[]>([])
    const classHeadingVisibility: string = heading.visibility?.value ? `snippet-brands__heading--${heading.visibility.value}` : ''

    useEffect(() => {
        if (!refSnippet) return
        fetchBrands(refSnippet, setBrands)
    }, [])

    return (
        <section className="snippet-brands" ref={refSnippet}>
            <div className="snippet-brands__container">
                <h2 className={`snippet-brands__heading ${classHeadingVisibility}`}>{heading.heading?.value}</h2>
                <div className="snippet-brands__carousel">
                    <Swiper
                        modules={[Navigation, Pagination, Autoplay]}
                        spaceBetween={60}
                        slidesPerView={5}
                        loop={true}
                        grabCursor={false}
                        freeMode={true}
                        allowTouchMove={false}
                        autoplay={{ delay: 0, disableOnInteraction: false, pauseOnMouseEnter: true }}
                        speed={2000}
                        watchSlidesProgress={true}
                        watchOverflow={true}
                        breakpoints={{
                            1200: {
                                slidesPerView: 6,
                            },
                            1400: {
                                slidesPerView: 7,
                            }
                        }}
                    >
                        { brands.map((brand: Brand) => (
                            <SwiperSlide className="snippet-brands__carousel-entry" key={brand.id}>
                                <a href={brand.targetUrl} 
                                className="snippet-brands__carousel-entry-link"
                                target='_blank'
                                rel='noopener noreferrer'
                                >
                                <img
                                    src={`/img/${brand.imageId}`}
                                    alt={brand.altTagText}
                                    loading="lazy"
                                    className="snippet-brands__carousel-entry-image"
                                /></a>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
                <div className="snippet-brands__entries">
                    { brands.map((brand: Brand) => (
                        <div className="snippet-brands__entry" key={brand.id}>
                            <a href={brand.targetUrl} 
                            className="snippet-brands__carousel-entry-link"
                            target='_blank'
                            rel='noopener noreferrer'
                            >
                            <img
                                src={`/img/${brand.imageId}`}
                                alt={brand.altTagText}
                                loading="lazy"
                                className="snippet-brands__entry-image"
                            /></a>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default SnippetBrands
